import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Alert,
  AlertDescription,
} from "../components/ui/alert";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "../components/ui/input-otp";
import {
  Phone,
  Check,
  X,
  AlertTriangle,
  ArrowLeft,
  Eye,
  EyeOff,
  KeyRound,
  Shield,
  Loader2,
  CreditCard,
} from "lucide-react";

import logo2 from "../assets/logo2.svg";
import apiService from "../services/apiService";

const validateSAMobileNumber = (phoneNumber: string): { isValid: boolean; message?: string } => {
  if (!phoneNumber.trim()) {
    return { isValid: false, message: "Phone number is required" };
  }
  const cleanNumber = phoneNumber.replace(/\s/g, "");
  const saPhonePattern = /^(\+27|27|0)[6-8][0-9]{8}$/;
  if (!saPhonePattern.test(cleanNumber)) {
    return { isValid: false, message: "Please enter a valid South African mobile number" };
  }
  return { isValid: true };
};

const formatPhoneNumber = (value: string): string => {
  const cleaned = value.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+27')) {
    const digits = cleaned.slice(3);
    if (digits.length <= 9) {
      if (digits.length >= 3) {
        return `+27 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`.trim();
      }
      return `+27 ${digits}`;
    }
  } else if (cleaned.startsWith('27')) {
    const digits = cleaned.slice(2);
    if (digits.length <= 9) {
      if (digits.length >= 3) {
        return `27 ${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`.trim();
      }
      return `27 ${digits}`;
    }
  } else if (cleaned.startsWith('0')) {
    const digits = cleaned.slice(1);
    if (digits.length <= 9) {
      if (digits.length >= 3) {
        return `0${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`.trim();
      }
      return `0${digits}`;
    }
  }
  return cleaned.slice(0, 13);
};

type Step = 'verify' | 'otp' | 'success';

export function ChangeMobilePage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('verify');
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [recoverySessionToken, setRecoverySessionToken] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const currentPhoneValidation = validateSAMobileNumber(currentPhoneNumber);
  const newPhoneValidation = validateSAMobileNumber(newPhoneNumber);
  const canSubmitVerify =
    currentPhoneValidation.isValid &&
    newPhoneValidation.isValid &&
    password.trim().length > 0 &&
    idNumber.trim().length >= 6 &&
    currentPhoneNumber.replace(/\s/g, "") !== newPhoneNumber.replace(/\s/g, "");

  const handleRequestRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitVerify) return;

    setError("");
    setIsLoading(true);

    try {
      const result = await apiService.requestMobileRecovery({
        currentPhoneNumber: currentPhoneNumber.replace(/\s/g, ""),
        newPhoneNumber: newPhoneNumber.replace(/\s/g, ""),
        password,
        idNumber: idNumber.replace(/\s/g, ""),
      });
      setRecoverySessionToken(result.recoverySessionToken);
      setSuccess(result.message || "OTP sent to your new mobile number.");
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start mobile number change. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length !== 6 || !recoverySessionToken) return;

    setError("");
    setIsLoading(true);

    try {
      await apiService.confirmMobileRecovery({
        recoverySessionToken,
        newPhoneNumber: newPhoneNumber.replace(/\s/g, ""),
        otp,
      });
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid or expired OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneInput = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => {
    setter(formatPhoneNumber(value));
    if (error) setError("");
  };

  const renderVerifyStep = () => (
    <form onSubmit={handleRequestRecovery} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="currentPhoneNumber" style={{ fontFamily: "Montserrat, sans-serif" }}>
          Current registered mobile number
        </Label>
        <div className="relative">
          <Input
            id="currentPhoneNumber"
            type="tel"
            placeholder="0XX XXX XXXX"
            value={currentPhoneNumber}
            onChange={(e) => handlePhoneInput(e.target.value, setCurrentPhoneNumber)}
            className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pl-12"
            style={{ height: "var(--mobile-touch-target)", fontFamily: "Montserrat, sans-serif" }}
            required
          />
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
        {currentPhoneNumber && !currentPhoneValidation.isValid && (
          <p className="text-sm text-red-500 flex items-center gap-1" style={{ fontFamily: "Montserrat, sans-serif" }}>
            <X className="w-3 h-3" />
            {currentPhoneValidation.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password" style={{ fontFamily: "Montserrat, sans-serif" }}>
          Password or USSD PIN
        </Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password or USSD PIN"
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
            className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pl-12 pr-12"
            style={{ height: "var(--mobile-touch-target)", fontFamily: "Montserrat, sans-serif" }}
            required
          />
          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="idNumber" style={{ fontFamily: "Montserrat, sans-serif" }}>
          SA ID or passport number
        </Label>
        <div className="relative">
          <Input
            id="idNumber"
            type="text"
            placeholder="As registered on your wallet"
            value={idNumber}
            onChange={(e) => { setIdNumber(e.target.value); if (error) setError(""); }}
            className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pl-12"
            style={{ height: "var(--mobile-touch-target)", fontFamily: "Montserrat, sans-serif" }}
            required
          />
          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="newPhoneNumber" style={{ fontFamily: "Montserrat, sans-serif" }}>
          New mobile number
        </Label>
        <div className="relative">
          <Input
            id="newPhoneNumber"
            type="tel"
            placeholder="0XX XXX XXXX"
            value={newPhoneNumber}
            onChange={(e) => handlePhoneInput(e.target.value, setNewPhoneNumber)}
            className="bg-white border-gray-200 focus:border-[#86BE41] focus:ring-[#86BE41] pl-12"
            style={{ height: "var(--mobile-touch-target)", fontFamily: "Montserrat, sans-serif" }}
            required
          />
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
        {newPhoneNumber && !newPhoneValidation.isValid && (
          <p className="text-sm text-red-500 flex items-center gap-1" style={{ fontFamily: "Montserrat, sans-serif" }}>
            <X className="w-3 h-3" />
            {newPhoneValidation.message}
          </p>
        )}
        {newPhoneNumber && newPhoneValidation.isValid && (
          <p className="text-sm text-green-500 flex items-center gap-1" style={{ fontFamily: "Montserrat, sans-serif" }}>
            <Check className="w-3 h-3" />
            Valid South African mobile number
          </p>
        )}
      </div>

      <Button
        type="submit"
        disabled={!canSubmitVerify || isLoading}
        className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] hover:from-[#7AB139] hover:to-[#2680B8] text-white disabled:opacity-60"
        style={{
          height: "var(--mobile-touch-target)",
          fontFamily: "Montserrat, sans-serif",
          fontWeight: "var(--font-weight-medium)",
        }}
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Verifying...
          </span>
        ) : (
          "Continue"
        )}
      </Button>
    </form>
  );

  const renderOtpStep = () => (
    <form onSubmit={handleConfirmRecovery} className="space-y-4">
      <div className="space-y-2">
        <Label style={{ fontFamily: "Montserrat, sans-serif" }}>
          Enter OTP
        </Label>
        <div className="flex justify-center">
          <InputOTP maxLength={6} value={otp} onChange={setOtp}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>
        <p className="text-sm text-gray-500 text-center" style={{ fontFamily: "Montserrat, sans-serif" }}>
          Enter the 6-digit code sent to your new mobile number
        </p>
      </div>

      <Button
        type="submit"
        disabled={otp.length !== 6 || isLoading}
        className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] hover:from-[#7AB139] hover:to-[#2680B8] text-white disabled:opacity-60"
        style={{ height: "var(--mobile-touch-target)", fontFamily: "Montserrat, sans-serif" }}
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Updating...
          </span>
        ) : (
          "Confirm new number"
        )}
      </Button>

      <button
        type="button"
        onClick={() => { setStep('verify'); setOtp(''); setError(''); setSuccess(''); }}
        className="w-full text-sm text-[#2D8CCA] hover:text-[#2680B8] underline"
        style={{ fontFamily: "Montserrat, sans-serif" }}
      >
        Back to details
      </button>
    </form>
  );

  const renderSuccessStep = () => (
    <div className="text-center space-y-4">
      <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
        <Check className="w-8 h-8 text-green-600" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900" style={{ fontFamily: "Montserrat, sans-serif" }}>
        Mobile number updated
      </h3>
      <p className="text-gray-600" style={{ fontFamily: "Montserrat, sans-serif" }}>
        Your wallet is now linked to your new number. Sign in with your new mobile number and the same password.
      </p>
      <Button
        onClick={() => navigate("/login")}
        className="w-full bg-gradient-to-r from-[#86BE41] to-[#2D8CCA] hover:from-[#7AB139] hover:to-[#2680B8] text-white"
        style={{ height: "var(--mobile-touch-target)", fontFamily: "Montserrat, sans-serif" }}
      >
        Sign in
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#86BE41] to-[#2D8CCA]">
      <div className="mobile-container">
        <div style={{ padding: "var(--mobile-padding)", paddingTop: "2rem", paddingBottom: "2rem" }}>
          <div className="text-center mb-6">
            <div className="flex justify-center mb-2">
              <img src={logo2} alt="MyMoolah Logo" style={{ height: "6rem", width: "auto", maxWidth: "300px" }} />
            </div>
            <h1 style={{ fontFamily: "Montserrat, sans-serif", fontSize: "clamp(1.25rem, 4vw, 1.75rem)", fontWeight: "var(--font-weight-bold)", color: "white" }}>
              Change Mobile Number
            </h1>
            <p style={{ fontFamily: "Montserrat, sans-serif", color: "rgba(255, 255, 255, 0.9)", marginTop: "0.5rem" }}>
              {step === 'verify' && "Verify your identity to update your registered number"}
              {step === 'otp' && "Confirm your new mobile number with OTP"}
              {step === 'success' && "Your number has been updated"}
            </p>
          </div>

          {error && (
            <Alert className="border-red-200 bg-red-50 mb-4">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription style={{ fontFamily: "Montserrat, sans-serif", color: "#dc2626" }}>
                {error}
              </AlertDescription>
            </Alert>
          )}
          {success && step === 'otp' && (
            <Alert className="border-green-200 bg-green-50 mb-4">
              <Check className="h-4 w-4 text-green-600" />
              <AlertDescription style={{ fontFamily: "Montserrat, sans-serif", color: "#059669" }}>
                {success}
              </AlertDescription>
            </Alert>
          )}

          <Card className="bg-white/95 backdrop-blur-sm border-0 shadow-xl">
            <CardHeader style={{ paddingBottom: "1rem" }}>
              <div className="flex items-center gap-2">
                {step !== 'success' && (
                  <button
                    type="button"
                    onClick={() => {
                      if (step === 'otp') {
                        setStep('verify');
                        setOtp('');
                        setError('');
                      } else {
                        navigate("/login");
                      }
                    }}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label="Go back"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <div className="flex-1">
                  <CardTitle className="text-center" style={{ fontFamily: "Montserrat, sans-serif" }}>
                    {step === 'verify' && "Verify account"}
                    {step === 'otp' && "Verify OTP"}
                    {step === 'success' && "Success"}
                  </CardTitle>
                  {step !== 'success' && (
                    <CardDescription className="text-center mt-1" style={{ fontFamily: "Montserrat, sans-serif" }}>
                      Step {step === 'verify' ? '1' : '2'} of 2
                    </CardDescription>
                  )}
                </div>
                {step !== 'success' && <div className="w-5" />}
              </div>
            </CardHeader>
            <CardContent>
              {step === 'verify' && renderVerifyStep()}
              {step === 'otp' && renderOtpStep()}
              {step === 'success' && renderSuccessStep()}
            </CardContent>
          </Card>

          <div className="mt-4 flex items-center justify-center gap-2 text-white/80">
            <Shield className="w-4 h-4" />
            <span style={{ fontFamily: "Montserrat, sans-serif", fontSize: "var(--mobile-font-small)" }}>
              Bank-grade security with ID verification and OTP
            </span>
          </div>

          {step !== 'success' && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-white/90 hover:text-white underline"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
